import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Command palette (Cmd/Ctrl+K). A single keyboard-first surface that lists the
 * editor's available actions — open/save/preview/search/undo/tools — filtered as
 * you type. The actions themselves live in the editor route (which owns both the
 * store actions and the local UI state); this component is a generic runner.
 */
export interface Command {
  readonly id: string;
  readonly title: string;
  readonly hint?: string; // shortcut or context, shown right-aligned
  readonly run: () => void;
}

interface Props {
  readonly commands: ReadonlyArray<Command>;
  readonly onClose: () => void;
}

export function CommandPalette({ commands, onClose }: Props): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => c.title.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keep the cursor in range as the filtered set shrinks/grows.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  function runAt(index: number): void {
    const cmd = filtered[index];
    if (!cmd) return;
    onClose();
    cmd.run();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (filtered.length ? (a + 1) % filtered.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (filtered.length ? (a - 1 + filtered.length) % filtered.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runAt(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div className="cmdk-backdrop" role="presentation" onClick={onClose}>
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="コマンドパレット"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          className="cmdk__input"
          placeholder="コマンドを検索…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="コマンドを検索"
          role="combobox"
          aria-expanded="true"
          aria-controls="cmdk-list"
        />
        <ul className="cmdk__list" id="cmdk-list" role="listbox">
          {filtered.length === 0 ? (
            <li className="cmdk__empty">一致するコマンドがありません</li>
          ) : (
            filtered.map((c, i) => (
              <li key={c.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  className={`cmdk__item ${i === active ? 'is-active' : ''}`.trim()}
                  onMouseMove={() => setActive(i)}
                  onClick={() => runAt(i)}
                >
                  <span className="cmdk__title">{c.title}</span>
                  {c.hint && <span className="cmdk__hint">{c.hint}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
